// @ts-strict-ignore
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { StringSchema, object as yupObject } from 'yup'
import { Avatar, ComboboxItem, ComboboxProps } from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useCountryStatesQuery } from 'hooks/queries/Customers/useCountryStates'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { CountryCodes, ErrorDetails } from 'types/types'
import { getFormattedSingleDateValue } from 'utils/date'
import { getCountryOptions, getFormObject } from 'utils/forms'
import {
  EMPLOYMENT_INDUSTRY_SECTOR_OPTIONS,
  ESTIMATED_YEARLY_INCOME_OPTIONS,
} from '../helpers/constants'
import {
  BASE_CUSTOMER_OPTIONS,
  FormKeyMapping,
  INDIVIDUAL_CURRENCIES,
  INDIVIDUAL_DESCRIPTION,
  INDIVIDUAL_EMPLOYMENT_STATUS,
  INDIVIDUAL_PEP_STATUS,
  INDIVIDUAL_SOURCE_OF_FUNDS,
  setFieldErrors,
} from '../helpers/form'
import {
  IndividualCustomerFormData,
  IndividualCustomerFormFields,
  IndividualCustomerFormKey,
  UseCase,
} from '../helpers/types'
import { getIndividualCustomerErrorKey, getIndividualFieldLabel } from '../helpers/utils'

type OptionItemsMapping = FormKeyMapping<ComboboxItem[]>
type OptionStateMapping = FormKeyMapping<boolean>

export const useIndividualCustomerForm = (
  defaultValues: IndividualCustomerFormData,
  fieldErrors?: ErrorDetails,
  paxosUsStates?: string[]
) => {
  const { t } = useTranslation()
  const { enableOptionalEpcFields } = useFeatureFlags()
  const { getYupField, getRequiredYupField, getEmailYupField, getConditionalYupField } =
    useYupFields()
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(defaultValues?.countryCode)

  const {
    data: allowedCountries = [],
    isFetching: isFetchingAllowedCountries,
    isError: isErrorFetchingAllowedCountries,
  } = useAllowedCountriesQuery()

  const {
    data: countries = [],
    isFetching: isFetchingCountries,
    isError: isErrorFetchingCountries,
  } = useCountriesQuery()

  const {
    data: countryStates = [],
    isFetching: isFetchingCountryStates,
    isError: isErrorFetchingCountryStates,
  } = useCountryStatesQuery(selectedCountryCode, selectedCountryCode === CountryCodes?.US)

  const allowedCountriesOptions = useMemo(
    () => getCountryOptions(allowedCountries),
    [allowedCountries]
  )
  const countryOptions = useMemo(() => getCountryOptions(countries), [countries])

  const getIndividualOptions = (options: Record<string, string>) =>
    Object.keys(options)?.map(key => ({
      value: options?.[key],
      leftText: t(`embeddedCustomers.individualOptions.${key}`),
    }))

  const employmentStatusOptions = useMemo(
    () => getIndividualOptions(INDIVIDUAL_EMPLOYMENT_STATUS),
    [t]
  )
  const pepStatusOptions = useMemo(() => getIndividualOptions(INDIVIDUAL_PEP_STATUS), [t])
  const sourceOfFundsOptions = useMemo(() => getIndividualOptions(INDIVIDUAL_SOURCE_OF_FUNDS), [t])

  const comboboxItems: OptionItemsMapping = {
    ...BASE_CUSTOMER_OPTIONS,
    intendedUseOfAccount: getIndividualOptions(INDIVIDUAL_DESCRIPTION),
    nationality: countryOptions,
    countryCode: allowedCountriesOptions,
    stateCode: countryStates,
    taxResidenceCountryCode: allowedCountriesOptions,
    expectedMonthlyVolumeCurrency: INDIVIDUAL_CURRENCIES?.map(currency => ({
      LeftElement: <Avatar name={currency} size="small" />,
      leftText: currency,
      value: currency,
    })),
    employmentStatus: employmentStatusOptions,
    pepStatus: pepStatusOptions,
    sourceOfFunds: sourceOfFundsOptions,
    estimatedYearlyIncome: ESTIMATED_YEARLY_INCOME_OPTIONS,
    employmentIndustrySector: EMPLOYMENT_INDUSTRY_SECTOR_OPTIONS,
  }

  const isComboboxLoading: OptionStateMapping = {
    nationality: isFetchingCountries,
    countryCode: isFetchingAllowedCountries,
    stateCode: isFetchingCountryStates,
    taxResidenceCountryCode: isFetchingAllowedCountries,
  }

  const isErrorLoadingOptions: OptionStateMapping = {
    nationality: isErrorFetchingCountries,
    countryCode: isErrorFetchingAllowedCountries,
    stateCode: isErrorFetchingCountryStates,
    taxResidenceCountryCode: isErrorFetchingAllowedCountries,
  }

  const formKeys = Object.keys(defaultValues) as IndividualCustomerFormKey[]

  const getFieldSchema = (key: IndividualCustomerFormKey): StringSchema => {
    const label = getIndividualFieldLabel(key)
    switch (key) {
      case 'emailAddress':
        return getEmailYupField()
      case 'addressLine2':
      case 'externalReference':
        return getYupField({ label })
      case 'addressLine1':
      case 'city':
      case 'postalCode':
      case 'taxResidenceCountryCode':
      case 'countryCode':
        return enableOptionalEpcFields ? getYupField({ label }) : getRequiredYupField({ label })
      case 'stateCode':
        return getConditionalYupField<IndividualCustomerFormKey>({
          label,
          fieldKey: 'countryCode',
          fieldValue: CountryCodes?.US,
        })
      case 'estimatedYearlyIncome':
      case 'employmentIndustrySector':
        return getConditionalYupField<IndividualCustomerFormKey>({
          label,
          fieldKey: 'stateCode',
          fieldValue: value => {
            if (!value || !Array.isArray(paxosUsStates) || !paxosUsStates?.length) {
              return false
            }
            return paxosUsStates?.includes(value)
          },
        })
      case 'description':
        return getRequiredYupField({
          label: t('embeddedCustomers.individualForm.customerDescription'),
        })
      default:
        return getRequiredYupField({ label })
    }
  }

  const formSchema = yupObject(getFormObject(formKeys, getFieldSchema))

  const form = useForm<IndividualCustomerFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupFormResolver<IndividualCustomerFormData>(formSchema),
  })
  const { handleSubmit, setValue, setError, trigger, control } = form
  const formValues = useWatch({ control })
  const formErrors = form?.formState?.errors
  const hasErrors = !!Object.keys(formErrors)?.length
  const { setInputField, setNumberInputField, setComboboxField, setSingleDateField } =
    useFormFields<IndividualCustomerFormKey>(formSchema, form, formValues)

  const handleComboboxChangeAdditional = (key: IndividualCustomerFormKey, value: string) => {
    switch (key) {
      case 'countryCode':
        setSelectedCountryCode(value)
        formErrors?.stateCode && trigger('stateCode')
        break
      case 'stateCode':
        if (formErrors?.estimatedYearlyIncome || formErrors?.employmentIndustrySector) {
          trigger(['estimatedYearlyIncome', 'employmentIndustrySector'])
        }
        break
      default:
        break
    }
  }

  const getFormField = (key: IndividualCustomerFormKey) => {
    if (Object.keys(comboboxItems)?.includes(key)) {
      const comboboxField: ComboboxProps = {
        ...(setComboboxField({
          key,
          items: comboboxItems?.[key],
          onChangeAdditional: (value: string) => handleComboboxChangeAdditional(key, value),
        }) as ComboboxProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: (formValues?.[key] as string) ?? '',
      }
      return comboboxField
    }
    if (key === 'dateOfBirth') {
      return setSingleDateField(key)
    }
    if (key === 'expectedMonthlyVolumeAmount') {
      return setNumberInputField({ key })
    }
    return {
      ...setInputField({ key }),
      inputMode: 'text',
      onChange: (event: ChangeEvent<HTMLInputElement>) =>
        setValue(key, event?.target?.value, { shouldValidate: true, shouldDirty: true }),
    }
  }

  const formFields = getFormObject<
    IndividualCustomerFormKey,
    ReturnType<typeof getFormField>,
    IndividualCustomerFormFields
  >(formKeys, getFormField)

  const getComboboxLabel = <LabelType extends string>(key: IndividualCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const displayData: Partial<IndividualCustomerFormData> = {
    dateOfBirth: getFormattedSingleDateValue(formValues?.dateOfBirth, DateFormat.DD_MMMM_YYYY),
    nationality: getComboboxLabel('nationality'),
    countryCode: getComboboxLabel('countryCode'),
    stateCode: getComboboxLabel('stateCode'),
    taxResidenceCountryCode: getComboboxLabel('taxResidenceCountryCode'),
    useCase: getComboboxLabel<UseCase>('useCase'),
    estimatedYearlyIncome: getComboboxLabel('estimatedYearlyIncome'),
    employmentIndustrySector: getComboboxLabel('employmentIndustrySector'),
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key]
  )

  const appliedFieldErrorsRef = useRef<ErrorDetails | undefined>(undefined)

  useEffect(() => {
    if (fieldErrors && fieldErrors !== appliedFieldErrorsRef.current) {
      setFieldErrors(fieldErrors, getIndividualCustomerErrorKey, setError)
      appliedFieldErrorsRef.current = fieldErrors
    }
  }, [fieldErrors, setError])

  useEffect(() => {
    if (optionErrorFields?.length) {
      optionErrorFields.forEach(key => {
        setError(key as IndividualCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [optionErrorFields?.length, setError, t])

  return { formFields, formValues, displayData, hasErrors, handleSubmit, form }
}
